import { useState } from 'react';
import { WAREHOUSE_LABELS, formatMoney, formatNumber } from '@tamas/shared';
import { Price } from '../components/Price';
import { PrintInvoiceLayout, type PrintInvoiceItem } from '../components/PrintInvoiceLayout';
import { cartCount, cartTotal, useCart } from '../store/cart';
import { Icon } from '../components/Icon';

interface Props {
  onCheckout: () => void;
  canViewPrices: boolean;
  hidePrintLayout?: boolean;
}

export function CartPanel({ onCheckout, canViewPrices, hidePrintLayout }: Props) {
  const lines = useCart((s) => s.lines);
  const setQty = useCart((s) => s.setQty);
  const remove = useCart((s) => s.remove);
  const clear = useCart((s) => s.clear);

  const total = cartTotal(lines);
  const count = cartCount(lines);
  const [notes, setNotes] = useState('');

  const WHOLESALE_THRESHOLD = 5_000_000;
  const progressPercent = Math.min(100, Math.floor((total / WHOLESALE_THRESHOLD) * 100));

  const handlePrint = () => {
    window.print();
  };

  const printItems: PrintInvoiceItem[] = lines.map(l => ({
    key: l.key,
    title: l.title,
    color: l.color,
    warehouse: l.warehouse,
    qty: l.qty,
    price: l.price
  }));

  const handleWhatsApp = () => {
    let text = `سلام، درخواست ثبت سفارش دارم:\n\n`;
    lines.forEach(l => {
      text += `- ${l.title} (${l.color || 'بدون رنگ'} - ${WAREHOUSE_LABELS[l.warehouse]}): ${l.qty} عدد\n`;
    });
    if (canViewPrices) text += `\nجمع کل: ${formatMoney(total)}\n`;
    else text += `\nقیمت پس از تأیید حساب همکاری اعلام می‌شود.\n`;
    if (notes) text += `\nتوضیحات: ${notes}\n`;
    window.open(`https://wa.me/989135006644?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <aside className="cart" id="cart">
      <div className="cart-title">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="bag" /> سبد خرید
        </span>
        {count > 0 && <span className="badge brand">{formatNumber(count)} عدد</span>}
        {lines.length > 0 && (
          <button type="button" className="btn ghost sm" onClick={clear}>
            خالی کردن
          </button>
        )}
      </div>

      {lines.length === 0 ? (
        <div className="cart-empty">
          سبد خرید شما خالی است
        </div>
      ) : (
        <>
          <div className="wholesale-progress" style={{ margin: "var(--ds-space-3) 0 14px", padding: "var(--ds-space-4)", backgroundColor: 'var(--card)', borderRadius: '12px', border: '1px solid var(--tamas-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: "var(--ds-space-2)", fontSize: "var(--ds-text-sm)", fontWeight: 700, color: 'var(--tamas-fg)' }}>
              <span>حد نصاب سفارش عمده</span>
              <span>{progressPercent}%</span>
            </div>
            <div style={{ height: '6px', backgroundColor: 'var(--tamas-border)', borderRadius: '3px', overflow: 'hidden', marginBottom: "var(--ds-space-2)" }}>
              <div style={{ width: `${progressPercent}%`, height: '100%', backgroundColor: progressPercent >= 100 ? '#10b981' : '#0ea5e9', transition: 'width 0.3s ease' }} />
            </div>
            <div style={{ fontSize: "var(--ds-text-xs)", color: 'var(--tamas-muted)', textAlign: 'center' }}>
              حداقل مبلغ سفارش: <Price amount={WHOLESALE_THRESHOLD} />
            </div>
          </div>

          <div className="cart-items">
            {lines.map((line) => (
              <div className="cart-item" key={line.key}>
                <div className="cart-item-title">{line.title}</div>
                <div className="cart-item-meta">
                  <span>{line.color || 'بدون رنگ'}</span>
                  <span>{WAREHOUSE_LABELS[line.warehouse]}</span>
                </div>
                <div className="qty">
                  <button type="button" onClick={() => setQty(line.key, line.qty + 1)} disabled={line.qty >= line.maxStock} aria-label="افزایش">
                    +
                  </button>
                  <span>{formatNumber(line.qty)}</span>
                  <button type="button" onClick={() => setQty(line.key, line.qty - 1)} aria-label="کاهش">
                    −
                  </button>
                  <span className={canViewPrices ? undefined : 'price-obscured'} style={{ marginInlineStart: 'auto', fontWeight: 800, color: 'var(--tamas-accent)' }}><Price amount={line.price * line.qty} /></span>
                  <button type="button" className="btn ghost sm" style={{ color: 'var(--danger)' }} onClick={() => remove(line.key)} aria-label="حذف">
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="total" style={{ borderTop: '2px dashed var(--tamas-border)', paddingTop: "var(--ds-space-4)", marginTop: "var(--ds-space-2)" }}>
            <span style={{ fontWeight: 800, fontSize: "var(--ds-text-lg)" }}>مجموع فاکتور</span>
            <span className={canViewPrices ? undefined : 'price-obscured'} style={{ color: 'var(--tamas-accent)', fontWeight: 900, fontSize: "var(--ds-text-lead)" }}><Price amount={total} /></span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: "var(--ds-space-2)", marginTop: "var(--ds-space-4)" }}>
            <textarea 
              placeholder="توضیحات سفارش (اختیاری)..." 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ width: '100%', padding: "var(--ds-space-3)", borderRadius: '8px', border: '1px solid var(--tamas-border)', fontSize: "var(--ds-text-sm)", resize: 'vertical', minHeight: '60px', fontFamily: 'inherit', background: 'var(--tamas-surface)', color: 'var(--tamas-fg)' }}
            />
            <button type="button" className="btn primary checkout" onClick={onCheckout} style={{ padding: "var(--ds-space-3)", fontSize: "var(--ds-text-md)", borderRadius: '8px' }}>
              ثبت سفارش نهایی
            </button>
            <button type="button" className="btn" onClick={handleWhatsApp} style={{ backgroundColor: '#10b981', color: 'white', padding: "var(--ds-space-3)", fontSize: "var(--ds-text-md)", borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: "var(--ds-space-2)", border: 'none' }}>
              ارسال سفارش در واتساپ
            </button>
            <button type="button" className="btn ghost" onClick={handlePrint} style={{ padding: '10px', fontSize: "var(--ds-text-sm)", borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: "var(--ds-space-2)", border: '1px solid var(--tamas-border)', color: 'var(--tamas-fg)' }}>
              پیش‌فاکتور چاپی
            </button>
          </div>
        </>
      )}

      {/* Hidden print layout, revealed only during window.print() via global CSS */}
      {!hidePrintLayout && (
        <PrintInvoiceLayout
          title="پیش‌فاکتور فروش"
          items={printItems}
          total={total}
          showStamp={false}
        />
      )}
    </aside>
  );
}
